/** Coordena a hidratação em background e compartilha uma única execução por conta/lista. */
import { countRecords, getList } from '../data/localDb';
import {
  hydrateListBackup,
  type HydrateListBackupResult,
} from './listHydration';
import type { ScanType } from '../types/searchList';

export const LIST_HYDRATION_LOCK_PREFIX = 'eminer:list-hydration:';

export type ListHydrationStatus = 'running' | 'complete' | 'error';

export interface ListHydrationState {
  listId: string;
  userId: string;
  status: ListHydrationStatus;
  module?: ScanType;
  loaded: number;
  total: number;
  error?: unknown;
}

interface BrowserLockManager {
  request<T>(
    name: string,
    options: { mode: 'exclusive' },
    callback: () => Promise<T>,
  ): Promise<T>;
}

const states = new Map<string, ListHydrationState>();
const runs = new Map<string, Promise<HydrateListBackupResult>>();
const deletedKeys = new Set<string>();
const memoryLockTails = new Map<string, Promise<void>>();
const listeners = new Set<() => void>();
let revision = 0;

function keyFor(listId: string, userId: string): string {
  return `${encodeURIComponent(userId)}:${encodeURIComponent(listId)}`;
}

function publish(state: ListHydrationState): void {
  states.set(keyFor(state.listId, state.userId), state);
  revision += 1;
  for (const listener of listeners) listener();
}

async function runWithMemoryLock<T>(name: string, task: () => Promise<T>): Promise<T> {
  const previous = (memoryLockTails.get(name) ?? Promise.resolve()).catch(() => undefined);
  let release!: () => void;
  const current = new Promise<void>((resolve) => {
    release = resolve;
  });
  const tail = previous.then(() => current);
  memoryLockTails.set(name, tail);
  await previous;
  try {
    return await task();
  } finally {
    release();
    if (memoryLockTails.get(name) === tail) memoryLockTails.delete(name);
  }
}

/** O mesmo lock é usado pela exclusão S10.1 para impedir publicação após a cascata. */
export async function withListHydrationLock<T>(
  listId: string,
  userId: string,
  task: () => Promise<T>,
): Promise<T> {
  const locks = typeof navigator === 'undefined'
    ? undefined
    : (navigator as Navigator & { locks?: BrowserLockManager }).locks;
  const name = `${LIST_HYDRATION_LOCK_PREFIX}${keyFor(listId, userId)}`;
  if (!locks) return runWithMemoryLock(name, task);
  return locks.request(
    name,
    { mode: 'exclusive' },
    task,
  );
}

async function completedLocalResult(
  listId: string,
  userId: string,
): Promise<HydrateListBackupResult | undefined> {
  const list = await getList(listId);
  if (!list || list.userId !== userId) return undefined;
  const count = await countRecords(list.module, listId);
  return {
    listId,
    module: list.module,
    serverCount: count,
    localCount: count,
    inserted: 0,
  };
}

/**
 * A hidratação continua mesmo se a tela desmontar. Duas chamadas na mesma aba compartilham a
 * Promise; entre abas, Web Locks faz a segunda esperar e reler o marcador LocalList antes de
 * tocar na rede. Sem Web Locks, insertIfAbsent mantém a operação idempotente.
 */
export function startListHydration(
  listId: string,
  userId: string,
  options: { retryError?: boolean } = {},
): Promise<HydrateListBackupResult> {
  const normalizedUserId = userId.trim();
  const key = keyFor(listId, normalizedUserId);
  if (deletedKeys.has(key)) return Promise.reject(new Error('list_deleted'));
  const running = runs.get(key);
  if (running) return running;

  const previous = states.get(key);
  if (previous?.status === 'error' && !options.retryError) {
    return Promise.reject(previous.error);
  }

  publish({
    listId,
    userId: normalizedUserId,
    status: 'running',
    module: previous?.module,
    loaded: previous?.loaded ?? 0,
    total: previous?.total ?? 0,
  });

  const promise = withListHydrationLock(listId, normalizedUserId, async () => {
    if (deletedKeys.has(key)) throw new Error('list_deleted');
    const completed = await completedLocalResult(listId, normalizedUserId);
    if (completed) return completed;

    return hydrateListBackup(listId, {
      userId: normalizedUserId,
      onProgress: (progress) => {
        if (deletedKeys.has(key)) return;
        publish({
          listId,
          userId: normalizedUserId,
          status: 'running',
          module: progress.module,
          loaded: progress.loaded,
          total: progress.total,
        });
      },
    });
  })
    .then((result) => {
      if (deletedKeys.has(key)) throw new Error('list_deleted');
      publish({
        listId,
        userId: normalizedUserId,
        status: 'complete',
        module: result.module,
        loaded: result.localCount,
        total: result.localCount,
      });
      return result;
    })
    .catch((error: unknown) => {
      if (deletedKeys.has(key)) throw error;
      const current = states.get(key);
      publish({
        listId,
        userId: normalizedUserId,
        status: 'error',
        module: current?.module,
        loaded: current?.loaded ?? 0,
        total: current?.total ?? 0,
        error,
      });
      throw error;
    })
    .finally(() => {
      if (runs.get(key) === promise) runs.delete(key);
    });

  runs.set(key, promise);
  return promise;
}

/**
 * Chamado somente depois que o backend confirmou a exclusão. Impede uma hidratação pendente da
 * mesma aba de tocar na rede e remove o progresso antigo; a Web Lock cobre execuções em voo e
 * outras abas antes da cascata local.
 */
export function markListHydrationDeleted(listId: string, userId: string): void {
  const key = keyFor(listId, userId.trim());
  deletedKeys.add(key);
  states.delete(key);
  revision += 1;
  for (const listener of listeners) listener();
}

export function getListHydrationState(
  listId: string,
  userId: string,
): ListHydrationState | undefined {
  return states.get(keyFor(listId, userId));
}

export function subscribeListHydration(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function getListHydrationRevision(): number {
  return revision;
}

/** Aguarda somente até haver uma página visível, conclusão ou falha; não espera a lista toda. */
export function waitForReadableHydration(
  listId: string,
  userId: string,
): Promise<ListHydrationState | undefined> {
  const readable = () => {
    const state = getListHydrationState(listId, userId);
    return !state || state.status !== 'running' || state.loaded > 0 ? state : undefined;
  };
  const current = readable();
  if (current || !getListHydrationState(listId, userId)) return Promise.resolve(current);

  return new Promise((resolve) => {
    const unsubscribe = subscribeListHydration(() => {
      const state = readable();
      if (!state) return;
      unsubscribe();
      resolve(state);
    });
  });
}

export function __resetListHydrationManager(): void {
  states.clear();
  runs.clear();
  deletedKeys.clear();
  memoryLockTails.clear();
  revision += 1;
  for (const listener of listeners) listener();
}
